import { useMemo } from 'react';

import { Controller, type FieldPath, type FieldValues } from 'react-hook-form';

import {
  FieldCheckbox,
  type FieldCheckboxProps,
} from '@/components/Form/FieldCheckbox';
import {
  FieldMarkdownEditor,
  type FieldMarkdownEditorProps,
} from '@/components/Form/FieldMarkdownEditor';

import { FieldCheckboxes, type FieldCheckboxesProps } from './FieldCheckboxes';
import {
  FieldCreatableCheckboxes,
  type FieldCreatableCheckboxesProps,
} from './FieldCreatableCheckBoxes';
import {
  CreatableInput,
  type FieldCreatableInputProps,
} from './FieldCreatableInput';
import {
  FieldCreatableSelect,
  type FieldCreatableSelectProps,
} from './FieldCreatableSelect';
import { FieldDate, type FieldDateProps } from './FieldDate';
import {
  FieldMultiSelect,
  type FieldMultiSelectProps,
} from './FieldMultiSelect';
import { FieldOtp, type FieldOtpProps } from './FieldOtp';
import { FieldPassword, type FieldPasswordProps } from './FieldPassword';
import {
  FieldPhoneNumber,
  type FieldPhoneNumberProps,
} from './FieldPhoneNumber';
import { FieldRadios, type FieldRadiosProps } from './FieldRadios';
import { FieldSelect, type FieldSelectProps } from './FieldSelect';
import { FieldText, type FieldTextProps } from './FieldText';
import { FieldTextarea, type FieldTextareaProps } from './FieldTextarea';
import { FormFieldContext } from './FormField.ctx';
import { type FieldCustomProps } from './FormField.types';

export const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props:
    | FieldCustomProps<TFieldValues, TName>
    | FieldTextProps<TFieldValues, TName>
    | FieldTextareaProps<TFieldValues, TName>
    | FieldSelectProps<TFieldValues, TName>
    | FieldCreatableSelectProps<TFieldValues, TName>
    | FieldMultiSelectProps<TFieldValues, TName>
    | FieldOtpProps<TFieldValues, TName>
    | FieldDateProps<TFieldValues, TName>
    | FieldPasswordProps<TFieldValues, TName>
    | FieldCheckboxProps<TFieldValues, TName>
    | FieldCheckboxesProps<TFieldValues, TName>
    | FieldRadiosProps<TFieldValues, TName>
    | FieldMarkdownEditorProps<TFieldValues, TName>
    | FieldPhoneNumberProps<TFieldValues, TName>
    | FieldPhoneNumberProps<TFieldValues, TName>
    | FieldCreatableInputProps<TFieldValues, TName>
    | FieldCreatableCheckboxesProps<TFieldValues, TName>
  // -- ADD NEW FIELD PROPS TYPE HERE --
) => {
  const getField = () => {
    switch (props.type) {
      case 'custom':
        return <Controller {...props} />;

      case 'text':
      case 'email':
      case 'number':
      case 'tel':
        return <FieldText {...props} />;

      case 'password':
        return <FieldPassword {...props} />;

      case 'textarea':
        return <FieldTextarea {...props} />;

      case 'otp':
        return <FieldOtp {...props} />;

      case 'select':
        return <FieldSelect {...props} />;

      case 'creatable-select':
        return <FieldCreatableSelect {...props} />;

      case 'multi-select':
        return <FieldMultiSelect {...props} />;

      case 'date':
        return <FieldDate {...props} />;

      case 'checkbox':
        return <FieldCheckbox {...props} />;

      case 'checkboxes':
        return <FieldCheckboxes {...props} />;

      case 'creatable-checkboxes':
        return <FieldCreatableCheckboxes {...props} />;

      case 'radios':
        return <FieldRadios {...props} />;

      case 'markdown':
        return <FieldMarkdownEditor {...props} />;

      case 'phone-number':
        return <FieldPhoneNumber {...props} />;

      case 'creatable-input':
        return <CreatableInput {...props} />;

      // -- ADD NEW FIELD COMPONENT HERE --
    }
  };

  const contextValue = useMemo(
    () => ({
      name: props.name,
      optionalityHint: props.optionalityHint,
      isDisabled: props.isDisabled,
    }),
    [props.name, props.optionalityHint, props.isDisabled]
  );

  return (
    <FormFieldContext.Provider value={contextValue}>
      {getField()}
    </FormFieldContext.Provider>
  );
};
